import React, { useEffect, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {Box, IconButton, Menu, Button, Toolbar, AppBar, Link, MenuItem, useTheme, Typography} from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import { ProfilMenuItem } from '../../profilMenuItem';
import { AppBarLogo } from '../AppBarLogo';
import { AuthContext } from '../../../contexts/authContext';
import '../../../assets/css/homepage.css';

// Navigation menu items
const NAVIGATION_ITEMS = [
  { key: 'home', label: 'Home', href: '/#home' },
  { key: 'features', label: 'Features', href: '/#features' },
  { key: 'demo', label: 'Demo', href: '/#demo' },
  { key: 'faq', label: 'FAQ', href: '/#faq' },
  { key: 'supportus', label: 'Support us! 🥰', href: '/#supportus' }
];

// Default sign-in button component
const SignInButton = () => (
  <Button 
    href="/login" 
    variant="contained" 
    color="primary" 
    sx={{ borderRadius: 20 }}
  >
    Sign in
  </Button>
);

export const MyAppBar = () => {
  const { user, userProfile, loading, profileLoading } = useContext(AuthContext);
  const [userProfileComponent, setUserProfileComponent] = useState(<SignInButton />);
  const [anchorElNav, setAnchorElNav] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();

  // Update user profile based on authentication state
  useEffect(() => {
    if (loading || profileLoading) return;
    
    if (user && userProfile) {
      const profileDataWithEmail = {
        ...userProfile,
        email: user.email,
        uid: user.uid,
      };
      setUserProfileComponent(<ProfilMenuItem userData={profileDataWithEmail} />);
    } else {
      setUserProfileComponent(<SignInButton />);
    }
  }, [user, loading, userProfile, profileLoading]);




  const handleOpenNavMenu = useCallback((event) => {
    setAnchorElNav(event.currentTarget);
  }, []);

  const handleCloseNavMenu = useCallback(() => {
    setAnchorElNav(null);
  }, []);

  return (
    <>
      {/* Mobile Layout */}
      <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
        <AppBar position="fixed" elevation={0} sx={{ bgcolor: (theme) => theme.palette.appBarBg.main }}>
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                sx={{ mr: 1 }}
              >
                <MenuRoundedIcon sx={{ color: theme.palette.menuIcon.main }} />
              </IconButton>
              <AppBarLogo isMobile={true} />
            </Box>
            <Box>
              {userProfileComponent}
            </Box>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{ 
                '& .MuiPaper-root': {
                    backgroundColor: theme.palette.profileMenu.background,
                    border: `1px solid ${theme.palette.profileMenu.border}`,
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    minWidth: '160px'
                }
            }}
            >
              {NAVIGATION_ITEMS.map(({ key, label, href }) => (
                <MenuItem 
                  key={key}
                  onClick={handleCloseNavMenu} 
                  component="a" 
                  href={href}
                  sx={{
                    color: theme.palette.profileMenu.text,
                    '&:hover': {
                        backgroundColor: theme.palette.profileMenu.hover,
                        color: theme.palette.primary.main
                    }
                }}
                >
                  <Typography 
                            textAlign="center" 
                            sx={{ 
                                fontSize: '14px',
                                fontWeight: 500
                            }}
                        >
                            {label}
                        </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Toolbar>
        </AppBar>
        <Toolbar sx={{ display: { xs: 'flex', md: 'none' } }} />
      </Box>

      {/* Desktop Layout */}
      <Box
        sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, }}
      >
        <AppBar position="fixed" elevation={0} sx={{bgcolor: (theme) => theme.palette.appBarBg.main, boxShadow: "none"}}>
        {/* Left: Logo and App Name */}
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <AppBarLogo/>
          
        {/* Center: Navigation Items */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          position: 'absolute', 
          left: '50%', 
          transform: 'translateX(-50%)' 
        }}>
          <ul className="flexNullCenter" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {NAVIGATION_ITEMS.map(({ key, label, href }) => (
              <li key={key} className="font15 flexNullCenter pointer">
                <Link style={{ padding: "10px 15px" }} href={href}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </Box>

        {/* Right: Profile Menu or Sign In Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', position: 'absolute', right: 24 }}>
            {userProfileComponent}
          </Box>
        </Toolbar>
        </AppBar>
        <Toolbar sx={{ display: { xs: 'none', md: 'flex' } }} />
      </Box>
    </>
  );
}